import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {
  NotificationThreadInboundActionType,
  toNotificationThreadInboundActionType
} from "./notification-thread-inbound-action-type";
import {
  NotificationThreadAssignmentStrategy,
  toNotificationThreadAssignmentStrategy
} from "./notification-thread-assignment-strategy";
import {
  NotificationThreadParticipantValidationMode,
  toNotificationThreadParticipantValidationMode
} from "./notification-thread-participant-validation-mode";

export const NotificationTemplateThreadConfigCodec = t.intersection([
  t.type({
    id: t.string,
    notificationTemplateId: t.string,
    inboundEnabled: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    notificationReceiverProfileId: t.string,
    inboundAddressPatternOverride: t.string,
    replyKeyPrefix: t.string,
    inboundActionType: t.string,
    inboundHandlerKey: t.string,
    threadExpiresAfterMinutes: t.number,
    autoCloseAfterMinutes: t.number,
    archiveAfterMinutes: t.number,
    assignmentStrategy: t.string,
    defaultAssignedToId: t.string,
    participantValidationMode: t.string,
    allowInboundAttachments: t.boolean,
    maxInboundAttachmentCount: t.number,
    maxInboundAttachmentSizeBytes: t.number,
    maxInboundAttachmentTotalSizeBytes: t.number,
    retentionDays: t.number,
    auditRequired: t.boolean,
    createdById: t.string,
    lastModifiedById: t.string,
  }),
]);

export type NotificationThreadTemplateThreadConfig = t.TypeOf<typeof NotificationTemplateThreadConfigCodec>;

@Exclude()
export class NotificationTemplateThreadConfigEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  notificationTemplateId!: string;

  @Expose()
  notificationReceiverProfileId!: string;

  @Expose()
  inboundEnabled!: boolean;

  @Expose()
  inboundAddressPatternOverride?: string;

  @Expose()
  replyKeyPrefix?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toNotificationThreadInboundActionType))
  inboundActionType?: NotificationThreadInboundActionType;

  @Expose()
  inboundHandlerKey?: string;

  @Expose()
  threadExpiresAfterMinutes?: number;

  @Expose()
  autoCloseAfterMinutes?: number;

  @Expose()
  archiveAfterMinutes?: number;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toNotificationThreadAssignmentStrategy))
  assignmentStrategy?: NotificationThreadAssignmentStrategy;

  @Expose()
  defaultAssignedToId?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toNotificationThreadParticipantValidationMode))
  participantValidationMode?: NotificationThreadParticipantValidationMode;

  @Expose()
  allowInboundAttachments?: boolean;

  @Expose()
  maxInboundAttachmentCount?: number;

  @Expose()
  maxInboundAttachmentSizeBytes?: number;

  @Expose()
  maxInboundAttachmentTotalSizeBytes?: number;

  @Expose()
  retentionDays?: number;

  @Expose()
  auditRequired?: boolean;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  readonly entityType = "NOTIFTEMPLTHREADCFG";

  get displayName(): Readonly<string> {
    return this.id;
  }

  constructor() {
  }

  static from(value: NotificationThreadTemplateThreadConfig): NotificationTemplateThreadConfigEntity {
    return plainToInstance(NotificationTemplateThreadConfigEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<NotificationThreadTemplateThreadConfig> {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSON(NotificationTemplateThreadConfigCodec, object);
  }

  asTypeValue(): NotificationThreadTemplateThreadConfig {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSONNow(NotificationTemplateThreadConfigCodec, object);
  }
}
